import Button from '@atlaskit/button';
import { Field } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/add';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import React, { useEffect, useRef, useState } from 'react';
import LabelComponent from '../LabelComponent';
import SelectInput from '../RepeatingSelect/RepeatingSelect';
import { RepeatingTextInput } from '../RepeatingTextInput/RepeatingTextInput';
import { AddInputFieldButton, SelectInputWrapper, TextBoxInputWrapper, TrashIconWrapper } from './RepeatingSelectWrapperStyle';
const OTHER_LABEL = 'Other';

// eslint-disable-next-line max-lines-per-function
const RepeatingSelectWithInputs = _ref => {
  let {
    validate,
    options,
    selectedOptionsFromParentState,
    otherValuesFromParentState,
    addDropdownButtonLabel,
    otherInputPlaceholder,
    fieldName,
    onChange,
    otherInputBoxLabel,
    otherInputBoxPlaceholder,
    otherInputBoxButtonText
  } = _ref;
  // Why?
  // there are 2 states
  // 1. data is loaded from parent (defaultValue)
  // 2. user has altered the data (local state)
  // once we have the data from parent, we want to use local as single source of truth
  // or once user has changed the data, then local will be our the single source of truth
  const isDataLoadedOnceOrUserAltered = useRef(false);

  // parent does not want the "Other" as a value
  // "Other" is limited to only this render component
  const onChangeWithoutOtherValue = list => {
    // do not send empty strings to parent
    // do not send "Other" to parent
    const trimmedList = list.filter(item => {
      return item !== OTHER_LABEL && item !== '';
    });

    // remove duplicates
    const uniqueList = [...new Set(trimmedList)];
    isDataLoadedOnceOrUserAltered.current = true;
    onChange(uniqueList);
  };
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [textBoxInputs, setTextBoxInputs] = useState([...otherValuesFromParentState]);
  const [selectedOptions, setSelectedOptions] = useState([...selectedOptionsFromParentState]);
  const reconcileOtherVisibility = (dropdownInputs, otherInputs) => {
    if (dropdownInputs.indexOf(OTHER_LABEL) > -1) {
      setIsOtherSelected(true);
      if (otherInputs.length === 0) {
        setTextBoxInputs(['']);
      }
    } else {
      setIsOtherSelected(false);
      setTextBoxInputs([]);
    }
  };

  // WHY? there should always be one dropdown when there is no data
  useEffect(() => {
    if (selectedOptions.length === 0) {
      setSelectedOptions(['']);
    }
  }, [selectedOptions]);

  //ON MOUNT
  useEffect(() => {
    setTextBoxInputs([...otherValuesFromParentState]);
    if (selectedOptionsFromParentState.length > 0) {
      const dropdownItemsWithOther = [...selectedOptionsFromParentState, OTHER_LABEL];
      setSelectedOptions(dropdownItemsWithOther);
      reconcileOtherVisibility(dropdownItemsWithOther, otherValuesFromParentState);
    } else {
      setSelectedOptions(selectedOptionsFromParentState);
      reconcileOtherVisibility(selectedOptionsFromParentState, otherValuesFromParentState);
    }
  }, []);

  // WHY?
  // update the "other" data from parent
  // update the 'dropdown' values from parent
  useEffect(() => {
    if (isDataLoadedOnceOrUserAltered.current === false && (otherValuesFromParentState.length > 0 || selectedOptionsFromParentState.length > 0)) {
      isDataLoadedOnceOrUserAltered.current = true;

      // when parent is has some data that is not predefined
      if ((otherValuesFromParentState === null || otherValuesFromParentState === void 0 ? void 0 : otherValuesFromParentState.length) > 0) {
        const selectedOptionsWithOtherValue = [...selectedOptionsFromParentState, OTHER_LABEL];
        setSelectedOptions(selectedOptionsWithOtherValue);
        reconcileOtherVisibility(selectedOptionsWithOtherValue, otherValuesFromParentState);
      } else {
        setSelectedOptions(selectedOptionsFromParentState);
      }
      setTextBoxInputs([...otherValuesFromParentState]);
    }
  }, [otherValuesFromParentState, selectedOptionsFromParentState, isDataLoadedOnceOrUserAltered]);
  const handleTextBoxOnChange = value => {
    setTextBoxInputs(value);
    onChangeWithoutOtherValue([...selectedOptions, ...value]);
  };
  const handleOnDropdownAdd = () => {
    setSelectedOptions([...selectedOptions, '']);
  };
  const getUnselectedOptions = index => {
    const unselectedOptions = options.filter(option => {
      return (
        // options that are not selected
        !selectedOptions.includes(option.value) ||
        // itself + unselected options
        !selectedOptions.includes(selectedOptions[index])
      );
    });
    return unselectedOptions;
  };
  const handleDropdownValueChange = (newValue, index) => {
    const newSelectedOptions = [...selectedOptions];
    const isOtherRemoved = newSelectedOptions[index] === OTHER_LABEL;
    const isNewValueOther = newValue === OTHER_LABEL;
    newSelectedOptions[index] = newValue;
    setSelectedOptions([...newSelectedOptions]);
    reconcileOtherVisibility(newSelectedOptions, textBoxInputs);
    if (isNewValueOther) {
      onChangeWithoutOtherValue([...newSelectedOptions]);
      return;
    }
    if (isOtherRemoved) {
      onChangeWithoutOtherValue([...newSelectedOptions]);
    } else onChangeWithoutOtherValue([...newSelectedOptions, ...textBoxInputs]);
  };
  const handleDropdownRemove = indexToRemove => {
    const newSelectedOptions = [...selectedOptions];
    const isOtherRemoved = newSelectedOptions[indexToRemove] === OTHER_LABEL;
    newSelectedOptions.splice(indexToRemove, 1);
    setSelectedOptions([...newSelectedOptions]);
    reconcileOtherVisibility(newSelectedOptions, textBoxInputs);
    if (isOtherRemoved) {
      onChangeWithoutOtherValue([...newSelectedOptions]);
    } else onChangeWithoutOtherValue([...newSelectedOptions, ...textBoxInputs]);
  };
  const TextBoxInput = isOtherSelected ? /*#__PURE__*/React.createElement(TextBoxInputWrapper, null, /*#__PURE__*/React.createElement(Field, {
    "aria-required": isOtherSelected,
    name: fieldName + 'Other',
    label: /*#__PURE__*/React.createElement(LabelComponent, {
      label: otherInputBoxLabel
    }),
    isRequired: true
  }, () => /*#__PURE__*/React.createElement(RepeatingTextInput, {
    value: textBoxInputs,
    placeholder: otherInputBoxPlaceholder,
    onChange: handleTextBoxOnChange,
    fieldName: fieldName + 'Other',
    buttonText: otherInputBoxButtonText,
    validate: validate
  }))) : null;
  const addButtonField = selectedOptions.length < options.length ? /*#__PURE__*/React.createElement(AddInputFieldButton, {
    iconBefore: /*#__PURE__*/React.createElement(AddIcon, {
      label: "",
      size: "small"
    }),
    onClick: handleOnDropdownAdd,
    testId: "addInputFields"
  }, addDropdownButtonLabel) : null;
  return /*#__PURE__*/React.createElement("div", null, selectedOptions.map((value, index) => /*#__PURE__*/React.createElement(SelectInputWrapper, {
    key: index
  }, /*#__PURE__*/React.createElement(SelectInput, {
    defaultValue: value,
    placeholder: otherInputPlaceholder,
    onChange: optionValue => handleDropdownValueChange(optionValue, index),
    options: getUnselectedOptions(index),
    fieldName: "".concat(fieldName, "_").concat(index),
    validate: validate
  }), selectedOptions.length > 1 && /*#__PURE__*/React.createElement(TrashIconWrapper, null, /*#__PURE__*/React.createElement(Button, {
    onClick: () => handleDropdownRemove(index),
    iconBefore: /*#__PURE__*/React.createElement(TrashIcon, {
      size: "medium",
      label: ""
    }),
    testId: "deleteDropdownFields"
  })))), addButtonField, TextBoxInput);
};
const RepeatingSelectWrapper = _ref2 => {
  let {
    defaultValue,
    placeholder,
    onChange,
    options,
    buttonText,
    fieldName,
    validate,
    otherInputBoxLabel,
    otherInputBoxPlaceholder,
    otherInputBoxButtonText
  } = _ref2;
  const otherValuesFromParentState = (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.filter(value => !(options !== null && options !== void 0 && options.find(option => option.value === value)))) || []; // get all selection options that are NOT PREDEFINED

  const selectedOptionsFromParentState = (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.filter(value => options === null || options === void 0 ? void 0 : options.find(option => option.value === value))) || []; // get all selection options that are not PREDEFINED

  return /*#__PURE__*/React.createElement(RepeatingSelectWithInputs, {
    validate: validate,
    options: options,
    selectedOptionsFromParentState: selectedOptionsFromParentState,
    otherValuesFromParentState: otherValuesFromParentState,
    addDropdownButtonLabel: buttonText,
    otherInputPlaceholder: placeholder,
    fieldName: fieldName,
    onChange: onChange,
    otherInputBoxLabel: otherInputBoxLabel,
    otherInputBoxPlaceholder: otherInputBoxPlaceholder,
    otherInputBoxButtonText: otherInputBoxButtonText
  });
};
export default RepeatingSelectWrapper;